import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { backendUrl } from '../config';

export const useModuleData = (modulePosition) => {
  const [moduleData, setModuleData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accessBlocked, setAccessBlocked] = useState(false);

  const loadModule = useCallback(async () => {
    if (!modulePosition) {
      setModuleData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    setAccessBlocked(false);

    try {
      // Always fetch full module data from Supabase to ensure we have all fields including dialect
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      
      if (!userId) {
        setError('User not authenticated');
        return;
      }

      // Check if this is a module ID (UUID) or position (number)
      const isUUID = typeof modulePosition === 'string' && modulePosition.includes('-');
      
      let query = supabase
        .from('modules_new')
        .select('*');
      
      if (isUUID) {
        // It's a module ID (classroom module) - check access via backend
        query = query.eq('id', modulePosition);
      } else {
        // It's a position (private module)
        query = query.eq('position', Number(modulePosition))
                     .eq('user_id', userId);
      }
      
      const { data, error: fetchError } = await query.single();

      if (fetchError) {
        setError('Error fetching module');
        return;
      }

      // If this is a classroom module, check access via backend
      if (data.classroom_id && isUUID) {
        const accessToken = session?.access_token;
        if (accessToken) {
          try {
            const response = await fetch(`${backendUrl}/check-module-access`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                moduleId: data.id,
                accessToken
              })
            });

            const accessData = await response.json();

            if (!response.ok || !accessData.canAccess) {
              setAccessBlocked(true);
              setError(accessData.error || 'Access to this module is blocked');
              setModuleData(null);
              return;
            }
          } catch (err) {
            console.error('Error checking module access:', err);
            // If check fails, still allow access but log error
          }
        }
      }

      setModuleData(data);
    } catch (err) {
      setError('Error fetching module');
    } finally {
      setLoading(false);
    }
  }, [modulePosition]);

  useEffect(() => {
    loadModule();
  }, [loadModule]);

  return {
    moduleData,
    loading,
    error,
    accessBlocked,
    refetch: loadModule,
  };
};
